import { createSignal, type Component, For } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { PanelBadge } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";

type WsusStatus = "all" | "needed" | "installed" | "failed";

interface WsusUpdate {
  UpdateId?: string;
  Title?: string;
  Classification?: string;
  IsApproved?: boolean;
  IsDeclined?: boolean;
  CreationDate?: string;
}

interface WsusClient {
  FullDomainName?: string;
  IPAddress?: string;
  OSDescription?: string;
  LastSyncTime?: string;
  LastReportedStatusTime?: string;
}

interface ListResult<T> {
  rows?: T[];
}

const STATUS_OPTIONS: { id: WsusStatus; label: string }[] = [
  { id: "all", label: "Tous" },
  { id: "needed", label: "Requises (approuvees)" },
  { id: "installed", label: "Installees" },
  { id: "failed", label: "Declinees / echec" },
];

function formatDate(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      return new Date(v).toLocaleString();
    } catch {
      return v;
    }
  }
  return String(v);
}

const WsusPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [status, setStatusFilter] = createSignal<WsusStatus>("all");
  const [filter, setFilter] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const updatesQuery = createToolQuery<ListResult<WsusUpdate>>(() => ({
    tool: "wsus_updates_list",
    params: { status: status(), limit: 200 },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const clientsQuery = createToolQuery<ListResult<WsusClient>>(() => ({
    tool: "wsus_computers_list",
    params: { limit: 200 },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  function applyFilter<T>(rows: T[]): T[] {
    const q = filter().trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      for (const k of Object.keys(row as object)) {
        const v = (row as Record<string, unknown>)[k];
        if (v == null) continue;
        const s = typeof v === "object" ? JSON.stringify(v) : String(v);
        if (s.toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }

  async function runUpdateAction(update: WsusUpdate, action: "approve" | "decline"): Promise<void> {
    const title = String(update.Title || "");
    const updateId = String(update.UpdateId || "").trim();
    if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(updateId)) {
      setActionStatus({ text: "UpdateId GUID manquant ou invalide.", level: "danger" });
      return;
    }
    let group: string | null = null;
    if (action === "approve") {
      const rawGroup = window.prompt("Groupe cible (vide = 'All Computers'):", "All Computers");
      if (rawGroup == null) return;
      group = String(rawGroup).trim() || "All Computers";
    }
    const label = action === "approve" ? "Approve" : "Decline";
    const level: "warn" | "danger" = action === "approve" ? "warn" : "danger";
    const ok = await confirm.confirm({
      title: `${label} - ${title.slice(0, 80)}`,
      body: `Confirmer l'action WSUS "${label}" sur ${updateId}${group ? ` (groupe ${group})` : ""} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? updateId : "",
    });
    if (!ok) return;
    const params: Record<string, unknown> = { updateId, action };
    if (group) params.group = group;
    setActionStatus({ text: `Execution ${label}...`, level: "info" });
    try {
      await tools.call("wsus_update_action", params, { userConfirmedAt: new Date().toISOString() });
      setActionStatus({ text: `Action ${label} sur ${updateId} reussie.`, level: "info" });
      await updatesQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const updateColumns: Column<WsusUpdate>[] = [
    {
      accessorKey: "UpdateId",
      header: "UpdateId",
      cell: (info) => <code style={{ "font-size": "11px" }}>{String(info.getValue() ?? "")}</code>,
    },
    { accessorKey: "Title", header: "Title" },
    { accessorKey: "Classification", header: "Classification" },
    {
      accessorKey: "IsApproved",
      header: "IsApproved",
      cell: (info) => {
        const v = !!info.getValue();
        return <PanelBadge intent={v ? "ok" : "muted"}>{v ? "Yes" : "No"}</PanelBadge>;
      },
    },
    {
      accessorKey: "IsDeclined",
      header: "IsDeclined",
      cell: (info) => {
        const v = !!info.getValue();
        return <PanelBadge intent={v ? "danger" : "muted"}>{v ? "Yes" : "No"}</PanelBadge>;
      },
    },
    {
      accessorKey: "CreationDate",
      header: "CreationDate",
      cell: (info) => formatDate(info.getValue()),
    },
  ];

  const clientColumns: Column<WsusClient>[] = [
    { accessorKey: "FullDomainName", header: "FullDomainName" },
    { accessorKey: "IPAddress", header: "IPAddress" },
    { accessorKey: "OSDescription", header: "OSDescription" },
    {
      accessorKey: "LastSyncTime",
      header: "LastSyncTime",
      cell: (info) => formatDate(info.getValue()),
    },
    {
      accessorKey: "LastReportedStatusTime",
      header: "LastReportedStatusTime",
      cell: (info) => formatDate(info.getValue()),
    },
  ];

  const toolbar = (
    <>
      <input
        type="text"
        class="panel-filter-input"
        placeholder="Filtre (titre, KB, etc)..."
        spellcheck={false}
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <select
        class="panel-filter-input panel-filter-input--narrow"
        title="Statut des mises a jour"
        value={status()}
        onChange={(e) => setStatusFilter(e.currentTarget.value as WsusStatus)}
      >
        <For each={STATUS_OPTIONS}>{(o) => <option value={o.id}>{o.label}</option>}</For>
      </select>
      <button
        type="button"
        class="btn btn--xs btn--info"
        onClick={() => {
          updatesQuery.refetch();
          clientsQuery.refetch();
        }}
      >
        Rafraichir
      </button>
    </>
  );

  const subtabs: SubTab[] = [
    {
      id: "updates",
      label: "Updates",
      render: () => (
        <DataTable<WsusUpdate>
          rows={applyFilter(updatesQuery.data?.rows ?? [])}
          columns={updateColumns}
          rowActions={(r) => (
            <>
              <button type="button" class="btn btn--xs btn--warn" onClick={() => runUpdateAction(r, "approve")}>
                Approve
              </button>
              <button
                type="button"
                class="btn btn--xs btn--danger"
                disabled={r.IsDeclined === true}
                onClick={() => runUpdateAction(r, "decline")}
              >
                Decline
              </button>
            </>
          )}
        />
      ),
    },
    {
      id: "clients",
      label: "Clients",
      render: () => <DataTable<WsusClient> rows={applyFilter(clientsQuery.data?.rows ?? [])} columns={clientColumns} />,
    },
  ];

  const statusBar = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (updatesQuery.isFetching) return { text: "Chargement...", level: "info" };
    if (updatesQuery.error)
      return {
        text: `Erreur: ${updatesQuery.error instanceof Error ? updatesQuery.error.message : String(updatesQuery.error)}`,
        level: "danger",
      };
    return undefined;
  };

  return <PanelLayout title="WSUS" subtabs={subtabs} toolbar={toolbar} status={statusBar()} />;
};

registerPanel("wsus", WsusPanel);
export default WsusPanel;
